/* eslint-disable react/display-name */
import React, {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react'
import SelectRC, { Option } from 'rc-select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faChevronDown } from '@fortawesome/pro-duotone-svg-icons'

import { compareItem } from '@/Components/DisplayItems/Utils/FilterSelect'

interface UserOption {
    id: string
    value: string
    label: React.ReactNode
}

interface UserSelectSearchProps {
    selected?: UserOption[]
    options: UserOption[]
    userLoading: boolean
    placeholder?: string
    itemId?: string
    errors?: string[]
    onSearch: (value: string) => void
    onChange: (selected: UserOption[]) => void
    getOptions: (callback?: (options: UserOption[]) => void) => void
}

interface UserFieldEditRef {
    focus: () => void
    blur: () => void
}
export const UserFieldSearch = forwardRef<
    UserFieldEditRef,
    UserSelectSearchProps
>(
    (
        {
            selected,
            options,
            userLoading,
            itemId,
            placeholder = 'Search...',
            errors,
            onSearch,
            onChange,
            getOptions,
        },
        ref
    ) => {
        const innerRef = useRef<any>(null)

        useImperativeHandle(ref, () => ({
            focus: () => {
                innerRef.current?.focus?.()
            },
            blur: () => {
                innerRef.current?.blur?.()
            },
        }))

        const [selectedOptions, setSelectedOptions] = useState<UserOption[]>(
            selected || []
        )

        useEffect(() => {
            setSelectedOptions(
                selected?.map((item) => ({
                    id: item.id,
                    value: item.id,
                    label: item.label,
                })) || []
            )
        }, [selected])

        const filteredOptions = options.filter(
            (option) =>
                !selectedOptions.some(
                    (selected) => selected.value === option.value
                )
        )

        const filterOptions = (input: string, option: any) => {
            const labelText = compareItem(option.props.children)
            return labelText.toLowerCase().includes(input.toLowerCase())
        }

        const handleChange = (selectedItems: any) => {
            if (!Array.isArray(selectedItems)) return

            const newSelected: UserOption[] = selectedItems
                .map((value) => {
                    // First try to find it in options
                    return (
                        options.find((o) => o.value === value) ||
                        // Fallback: keep it from already selected options
                        selectedOptions.find((o) => o.value === value)
                    )
                })
                .filter(Boolean) as UserOption[]

            setSelectedOptions(newSelected)
            onChange(newSelected)
        }

        return (
            <SelectRC
                ref={innerRef}
                className={`w-100 custom-select-react ${
                    errors?.length ? 'border-red' : ''
                }`}
                id={`${itemId}-search`}
                mode="multiple"
                showSearch
                onSearch={onSearch}
                optionFilterProp="children"
                allowClear
                loading={userLoading}
                placeholder={placeholder}
                value={
                    selectedOptions.length > 0
                        ? selectedOptions.map((item) => ({
                              value: item.value,
                              label: item.label,
                          }))
                        : undefined
                }
                onChange={handleChange}
                filterOption={filterOptions}
                suffixIcon={
                    <FontAwesomeIcon
                        spin={userLoading}
                        icon={userLoading ? faSpinner : faChevronDown}
                    />
                }
                notFoundContent="No items found"
                onClick={() => getOptions((opts) => undefined)}
            >
                {filteredOptions.map((option) => (
                    <Option key={option.id} value={option.value}>
                        {option.label}
                    </Option>
                ))}
            </SelectRC>
        )
    }
)
